import React, { Fragment } from 'react';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { FieldArray, useFormikContext, FastField } from 'formik';
import times from 'lodash/times';
import { TextField, Select, Checkbox } from 'components/form/Formik/index';
import FormikMention from './TextareaWithExerciseMention';
import HelpContent from './HelpContent';

const QuestionsColumn = React.memo(({ parent, index, remove }) => {
  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item md={6}>
        <TextField
          autoFocus
          variant="outlined"
          margin="dense"
          name={`${parent}.header`}
          placeholder="Column header"
        />
      </Grid>
      <Grid item md={2}>
        <IconButton
          color="secondary"
          variant="outlined"
          onClick={() => remove(index)}
        >
          <DeleteForeverIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
});

const QuestionsRow = React.memo(({ parent, index, remove }) => {
  return (
    <div>
      <Grid container spacing={1} alignItems={'center'}>
        <Grid item md={6}>
          <TextField
            variant="outlined"
            margin="dense"
            name={`${parent}.header`}
            placeholder="Row label"
          />
        </Grid>
        <Grid item md={2}>
          <IconButton color="secondary" onClick={() => remove(index)}>
            <DeleteForeverIcon />
          </IconButton>
        </Grid>
      </Grid>
    </div>
  );
});

const QuestionsTableBlock = ({ index, block }) => {
  const { setFieldValue } = useFormikContext();
  const layout = block.layout;

  const onLayoutChange = (e) => {
    const { value } = e.target;
    if (value === 'row') {
      setFieldValue(`blocks.${index}.columnsCount`, 2);
      setFieldValue(`blocks.${index}.rows`, [{}]);
      setFieldValue(`blocks.${index}.rowsCount`, null);
      setFieldValue(`blocks.${index}.columns`, null);
    } else {
      setFieldValue(`blocks.${index}.rowsCount`, 1);
      setFieldValue(`blocks.${index}.columns`, [{}]);
      setFieldValue(`blocks.${index}.columnsCount`, null);
      setFieldValue(`blocks.${index}.rows`, null);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <FormikMention
          name={`blocks.${index}.description`}
          label="Optional description/help text"
        />
      </div>
      <Grid container spacing={2}>
        <Grid item md={6}>
          <Select name={`blocks.${index}.layout`} onChange={onLayoutChange}>
            <option disabled value="" selected>
              Select Layout
            </option>
            <option value="column">Column Layout</option>
            <option value="row">Row Layout</option>
          </Select>
        </Grid>
      </Grid>
      <Typography gutterBottom variant="subtitle2" color="textSecondary">
        {!layout
          ? 'Please select a layout'
          : layout === 'row'
          ? 'Use row layout. You can add more than one columns if you need to collect different answers for each row'
          : 'Use column layout. Each column has one question and any number of answers (configure number of rows)'}
      </Typography>
      {layout === 'column' && (
        <FieldArray
          name={`blocks.${index}.columns`}
          render={(helper) => (
            <div>
              <Grid container spacing={3} alignItems="flex-end">
                <Grid item md={3}>
                  <TextField
                    variant="outlined"
                    margin="dense"
                    name={`blocks.${index}.rowsCount`}
                    label="Number of rows"
                    type="number"
                  />
                </Grid>
                <Grid item md={6}>
                  <Checkbox
                    name={`blocks.${index}.allowAddRows`}
                    label="Allow user to add more rows"
                  />
                </Grid>
              </Grid>
              {block.columns?.map((col, i) => (
                <QuestionsColumn
                  column={col}
                  index={i}
                  key={i}
                  parent={`blocks.${index}.columns.${i}`}
                  remove={helper.remove}
                />
              ))}
              <Button
                color="primary"
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => helper.push({})}
              >
                Add column
              </Button>
            </div>
          )}
        />
      )}
      {layout === 'row' && (
        <FieldArray
          name={`blocks.${index}.rows`}
          render={(helper) => (
            <div>
              <Grid container>
                <Grid item md={5}>
                  <TextField
                    variant="outlined"
                    margin="dense"
                    name={`blocks.${index}.columnsCount`}
                    label="Number of columns"
                    type="number"
                    helpText="Use 2 columns minimum, 1st column displays label"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                {times(block.columnsCount).map((i) => (
                  <Grid item key={i} xs>
                    {i === 0 && (
                      <TextField
                        style={{ width: 140, whiteSpace: 'nowrap' }}
                        autoFocus
                        variant="outlined"
                        margin="dense"
                        name={`blocks.${index}.columns.${i}.header`}
                        placeholder="Column label"
                      />
                    )}
                    {i > 0 && (
                      <TextField
                        style={{
                          width: 130
                        }}
                        variant="outlined"
                        margin="dense"
                        name={`blocks.${index}.columns.${i}.header`}
                        placeholder="Column label"
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
              <br />
              <Typography variant="subtitle2" gutterBottom>
                Rows
              </Typography>
              {block.rows?.map((row, i) => (
                <QuestionsRow
                  index={i}
                  key={i}
                  parent={`blocks.${index}.rows.${i}`}
                  remove={helper.remove}
                />
              ))}
              <Box marginBottom={2}>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={() => helper.push({})}
                >
                  Add row
                </Button>
              </Box>
            </div>
          )}
        />
      )}
      <HelpContent name={`blocks.${index}.helpContent`} />
    </div>
  );
};

export default QuestionsTableBlock;
